<template>
  <div class="without-template">
    <div v-if="data.image" class="without-template__image">
      <TemplateMediaView
        :src="data.image"
        :aspect-width="1312"
        :aspect-height="730"
      />
    </div>
    <div class="without-template__text-container">
      <BaseTypography with-pre-line type="h2">
        {{ data.shortDescription }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.detailedDescription }}
      </BaseTypography>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IWithoutTemplateData, TemplateData } from '~/entities/projects/model/templates-interface'
import { computed } from 'vue'
import { useProjectRouteRef } from '~/entities/projects/model/useProjectRouteRef'
import { useProjectPublicQuery } from '~/shared/api'
import { isProjectRequestRouteQuery } from '~/shared/const'
import { BaseTypography } from '~/shared/ui'
import TemplateMediaView from './TemplateMediaView.vue'

const props = withDefaults(defineProps<Props>(), {
  templateData: null,
})
const route = useRoute()
const { projectId } = useProjectRouteRef(route)
const { result: projectData } = useProjectPublicQuery(
  () => ({ id: projectId.value }),
  () => ({
    fetchPolicy: 'no-cache',
    enabled:
      Boolean(projectId.value)
      && !String(route.name ?? '').includes('templates')
      && !isProjectRequestRouteQuery(route.query),
  }),
)

interface Props {
  templateData?: TemplateData | null
}

const data = computed(() => {
  if (props.templateData) {
    return props.templateData as IWithoutTemplateData
  }

  const isProjectPage = route.name?.toString().includes('templates')

  if (!isProjectPage) {
    const project = projectData.value?.publicProject
    return {
      shortDescription: project?.name,
      detailedDescription: project?.shortDescription,
      image: project?.media?.image?.DESKTOP?.OPEN_PROJECT,
    }
  }

  return {
    shortDescription: 'Как заполнить этот шаблон',
    detailedDescription: 'Заголовок - Напишите короткую фразу из 3–7 слов. Она должна быть простой и понятной.\nЗаголовок должен передавать позитивное настроение. Например: про новый день, вдохновение, движение вперёд, радость, развитие.\nНе используйте сложные слова и длинные предложения.\n\nНапишите текст объёмом до 1000 символов. Лучше разделить его на 2–3 абзаца, чтобы текст было удобно читать. Один абзац — это 2–4 предложения.\nПишите простым языком. Представьте, что вы объясняете мысль другу. Текст должен быть лёгким, понятным и нейтральным. Он не должен быть привязан к конкретному продукту, компании или услуге. Это универсальный текст, который используется как пример для шаблона.\nМожно писать о вдохновении, новых возможностях, развитии, спокойствии, ценности момента, движении вперёд или простых радостях жизни.\nСтарайтесь:\n\nне использовать сложные профессиональные термины\nне добавлять агрессивную рекламу\nне писать слишком длинные предложения\nне перегружать текст цифрами и фактами\n\nВажно, чтобы текст выглядел аккуратно в блоке и легко воспринимался при быстром чтении.',
    image: '/projects/project-template-1-bg-1.webp',
  }
})
</script>

<style lang="scss" scoped>
.without-template {
  display: flex;
  flex-direction: column;
  gap: rem(60px);

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(40px);
  }

  &__text-container {
    display: flex;
    flex-direction: column;
    gap: rem(16px);
  }

  &__image {
    overflow: hidden;
    border-radius: rem(16px);
    width: 100%;
  }
}
</style>
